/**
 * The subset of SecureChannel events this helper listens to. Duck-typed so
 * tests can pass a bare EventEmitter and runners can pass a real channel.
 */
export interface LifecycleChannel {
  on(ev: "auth_failed", cb: (e: { reason: string }) => void): unknown;
  on(ev: "session_replaced", cb: (e: { code?: number }) => void): unknown;
  on(ev: "error", cb: (err: unknown) => void): unknown;
}

/** Operator-facing re-enroll message, agent-tagged (mirrors Hermes lifecycle.py). */
export function terminalReenrollMessage(agentName: string, reason: string): string {
  const who = agentName ? `[${agentName}] ` : "";
  return (
    `${who}device is no longer valid (${reason}) — it was revoked or replaced. ` +
    `Re-enroll with a fresh token from AgentVault; the old credentials are dead. ` +
    `This agent will stop reconnecting.`
  );
}

/**
 * Operator-facing wording for a non-terminal channel `error` event (#718).
 *
 * The listeners that call this exist so Node's EventEmitter cannot throw on
 * `emit("error")` — which made "non-fatal" accurate about the PROCESS and
 * actively misleading about the MESSAGE. A server rejection arriving there
 * means a frame the log already reported as `result=sent` was refused and
 * dropped. wren accumulated **707** `unknown group` rejections between
 * 2026-07-02 and 2026-07-31, every one filed under the same wording as a
 * transient reconnect blip, and the owner received none of those replies.
 *
 * Server rejections are therefore named as delivery failures, and say that
 * nothing retries them: `_reconcileConversationGroups` (#719) repairs the
 * conversation→group map so the NEXT message lands, but it does not resend the
 * one that was refused. Everything else keeps the survive-and-reconnect
 * framing, narrowed to say what it is non-fatal *to*.
 */
export function formatChannelError(err: unknown): string {
  const msg = String(err);
  const rejection = /Server: (.+)$/.exec(msg);
  if (rejection) {
    return (
      `[AgentVault] DELIVERY FAILED — the server refused this frame: ` +
      `${rejection[1]}. The connection survives; this message was dropped ` +
      `and nothing retries it.`
    );
  }
  return `[AgentVault] channel error (non-fatal to the connection): ${msg}`;
}

/**
 * Wire terminal lifecycle events for a gateway-hosted agent. On a TERMINAL
 * condition (device revoked/replaced, session taken over, or the channel's
 * own "Reconnect loop detected" give-up error) we log a clear re-enroll
 * message and invoke `onTerminal` — which the runner uses to stop THIS
 * agent's channel and release it, leaving the process + other agents alive.
 *
 * All OTHER `error` events are transient and are deliberately left to the
 * channel's built-in reconnect (and the runner's own survive-log `error`
 * listener) — we never stop on a generic error (would regress #387 survival).
 *
 * This is the per-agent variant of the native bridge's attachLifecycle
 * (packages/claude-room-bridge/src/bridge.ts), which exits the process
 * because it hosts a single agent.
 */
export function attachLifecycle(
  channel: LifecycleChannel,
  opts: {
    agentName?: string;
    log?: (msg: string) => void;
    onTerminal?: (reason: string) => void | Promise<void>;
  } = {},
): void {
  const log = opts.log ?? (() => {});
  const agentName = opts.agentName ?? "";
  const onTerminal = opts.onTerminal ?? (() => {});
  let fired = false;
  const terminal = (reason: string): void => {
    if (fired) return; // idempotent: first terminal wins
    fired = true;
    log(terminalReenrollMessage(agentName, reason));
    // Never let a throwing/rejecting onTerminal surface as an unhandled
    // rejection (the runners wrap stop() in try/catch, but stay robust to a
    // future caller that forgets).
    void Promise.resolve(onTerminal(reason)).catch(() => {});
  };
  channel.on("auth_failed", (e: { reason: string }) => terminal(e.reason));
  channel.on("session_replaced", (e: { code?: number }) =>
    terminal(e?.code ? `session_replaced (${e.code})` : "session_replaced"),
  );
  channel.on("error", (err: unknown) => {
    // SecureChannel surfaces two terminal conditions as a generic `error`
    // (not a dedicated event): the operational `device_revoked` WS event
    // (channel.ts `_handleError("Device was revoked")`) and the give-up
    // "Reconnect loop detected" cascade. Everything else is transient and is
    // left to reconnect — never stopped.
    const msg = err instanceof Error ? err.message : String(err);
    if (/reconnect loop detected/i.test(msg)) terminal("reconnect_loop");
    else if (/device (was )?revoked/i.test(msg)) terminal("device_revoked");
  });
}
